import type { ReactElement } from "react";
export { ACCOUNT_CREATION_DISABLED_NOTICE, isAccountCreationDisabled } from "./site-flags";
export {
  AbExperimentContext,
  usePageExperiment,
  type AbVariant,
  type AbExperimentContextValue,
} from "./ab-experiment-context";
export {
  resolvePaymentCheckoutPriceId,
  startPaymentCheckout,
  type PaymentCheckoutRequest,
} from "./payment-checkout";
export {
  buildVerificationPagePath,
  buildVerificationPageUrl,
  buildVerificationResumeToken,
  isVerificationResumeCheckout,
  parseVerificationResumeToken,
  type VerificationResumeCheckoutToken,
} from "./verification-resume";

export const ProductCapability = {
  Dashboard: 1 << 0,
  AdminSettings: 1 << 1,
  BackgroundJob: 1 << 2,
  Routes: 1 << 3,
  Billing: 1 << 4,
  Addons: 1 << 5,
} as const;

export type ProductCapability = (typeof ProductCapability)[keyof typeof ProductCapability];

export type ProductCapabilities = number & { __brand: "ProductCapabilities" };

export function combineCapabilities(...capabilities: ProductCapability[]): ProductCapabilities {
  return capabilities.reduce((mask, capability) => mask | capability, 0) as ProductCapabilities;
}

export function hasCapability(mask: ProductCapabilities, capability: ProductCapability): boolean {
  return (mask & capability) === capability;
}

// ---------------------------------------------------------------------------
// Billing & pricing
// ---------------------------------------------------------------------------

export type BillingModel = "per-seat" | "flat" | "usage";

export type BillingInterval = "month" | "year" | "one-time";

export type ProductPricing = {
  /** Price in the billing currency's minor unit, typically cents. */
  price: number;
  billingModel: BillingModel;
  interval: BillingInterval;
  priceDimensions?: string[];
};

export interface BillingConfig {
  billingModel: BillingModel;
  priceDimensions?: string[];
  pricing?: ProductPricing;
  addons?: ProductAddon[];
}

// ---------------------------------------------------------------------------
// Add-ons
// ---------------------------------------------------------------------------

export type AddonMarketingContent = {
  summary: string;
  description: string;
  ctaLabel: string;
};

/** Standalone add-on product IDs — not tied to any single offering. */
export const ADDON_API_ACCESS_ID = "cf-api-access" as const;
export const ADDON_EXTENDED_STORAGE_ID = "cf-extended-storage" as const;

export type ProductAddon = {
  id: string;
  name: string;
  description: string;
  pricing: ProductPricing;
  marketing?: AddonMarketingContent;
};

export type AddonRoutes = {
  detailPath: string;
  signupPath?: string;
  settingsPath?: string;
};

export type OfferingAddonEnrollmentHook = {
  onEnroll?: (input: {
    userId: number;
    productId: string;
    addonId: string;
    data?: unknown;
  }) => Promise<void>;
  onUnenroll?: (input: { userId: number; productId: string; addonId: string }) => Promise<void>;
};

/**
 * A fully-wired add-on that can render its own UI.
 * `TDbClient` and `TRendered` mirror the parent plugin's generics so add-on
 * pages can share the same rendering and data layer.
 */
export type ProductAddonPlugin<TDbClient = unknown, TRendered = unknown> = ProductAddon & {
  routes: AddonRoutes;
  detailPage(dbClient: TDbClient): TRendered;
  settingsPage?(dbClient: TDbClient): TRendered;
  signupPage?(dbClient: TDbClient): TRendered;
};

// ---------------------------------------------------------------------------
// Manifest
// ---------------------------------------------------------------------------

export interface ProductManifest {
  version: string;
  capabilities: ProductCapabilities;
  billingModel?: BillingModel;
  pricing?: ProductPricing;
  addons?: ProductAddon[];
}

export interface OfferingContext {
  userId?: string;
  orgId?: string;
  isAdmin: boolean;
}

export interface EntitlementService {
  isEnabled(offeringId: string, orgId: string): Promise<boolean>;
  hasFeature(offeringId: string, orgId: string, feature: string): Promise<boolean>;
  quotaRemaining(offeringId: string, orgId: string, quotaKey: string): Promise<number>;
}

export interface UsageEvent {
  offeringId: string;
  orgId: string;
  dimension: string;
  amount: number;
}

export interface DomainEvent {
  type: string;
  payload: unknown;
  idempotencyKey?: string;
  occurredAt?: string;
}

export interface EventBus {
  publish(event: DomainEvent): Promise<void>;
  subscribe(eventType: string, handler: (event: DomainEvent) => Promise<void>): void;
}

export type PlatformCapability =
  | "background-workers"
  | "canary-deployments"
  | "internal-service-network"
  | "managed-object-storage"
  | "managed-postgres"
  | "managed-redis"
  | "metrics-scrape"
  | "public-ingress"
  | "secret-store"
  | "service-discovery";

export interface PlatformDescriptor {
  id: string;
  name: string;
  capabilities: PlatformCapability[];
}

export type DeploymentExposure = "public" | "internal" | "private";

export type DeploymentWorkloadKind = "http" | "worker" | "job";

export type DeploymentProtocol = "http" | "tcp";

export type DependencyLifecycle = "managed" | "self-hosted" | "external";

export type DeploymentDependencyKind =
  | "event-bus"
  | "object-storage"
  | "postgres"
  | "redis"
  | "secret-store";

export interface DeploymentPort {
  name: string;
  port: number;
  protocol?: DeploymentProtocol;
  healthCheckPath?: string;
}

export interface DeploymentMetricsEndpoint {
  path: string;
  portName?: string;
  exposure?: DeploymentExposure;
}

export interface DeploymentScaling {
  minReplicas: number;
  maxReplicas?: number;
}

export interface DeploymentWorkload {
  id: string;
  kind: DeploymentWorkloadKind;
  appPackage?: string;
  startScript?: string;
  exposure?: DeploymentExposure;
  ports?: DeploymentPort[];
  metrics?: DeploymentMetricsEndpoint;
  scaling?: DeploymentScaling;
  dependencyIds?: string[];
}

export interface DeploymentDependency {
  id: string;
  kind: DeploymentDependencyKind;
  lifecycle?: DependencyLifecycle;
  required?: boolean;
}

export interface DeploymentTopology {
  workloads: DeploymentWorkload[];
  dependencies?: DeploymentDependency[];
}

export interface DeploymentManifest {
  sdkVersionRange: string;
  minWorkerVersion?: string;
  migrationsRequired?: boolean;
  requirements?: PlatformCapability[];
  topology?: DeploymentTopology;
}

export interface PlatformCompatibilityReport {
  platformId: string;
  compatible: boolean;
  requiredCapabilities: PlatformCapability[];
  missingCapabilities: PlatformCapability[];
}

export interface PlatformRenderedWorkload {
  workloadId: string;
  resourceType: string;
  resourceName: string;
  settings: Record<string, unknown>;
}

export interface PlatformRenderedDependency {
  dependencyId: string;
  resourceType: string;
  resourceName: string;
  settings: Record<string, unknown>;
}

export interface PlatformDeploymentPlan {
  platformId: string;
  compatible: boolean;
  missingCapabilities: PlatformCapability[];
  workloads: PlatformRenderedWorkload[];
  dependencies: PlatformRenderedDependency[];
}

export interface PlatformAdapter {
  descriptor: PlatformDescriptor;
  assess(deployment: DeploymentManifest): PlatformCompatibilityReport;
  render(deployment: DeploymentManifest): PlatformDeploymentPlan;
}

export function inferPlatformCapabilities(deployment?: DeploymentManifest): PlatformCapability[] {
  if (!deployment) return [];

  const capabilities = new Set<PlatformCapability>(deployment.requirements ?? []);
  const topology = deployment.topology;
  if (!topology) {
    return [...capabilities].sort();
  }

  for (const workload of topology.workloads) {
    if (workload.kind === "worker" || workload.kind === "job") {
      capabilities.add("background-workers");
    }

    if (workload.exposure === "public") {
      capabilities.add("public-ingress");
    }

    if (workload.exposure === "internal" || workload.exposure === "private") {
      capabilities.add("internal-service-network");
      capabilities.add("service-discovery");
    }

    if (workload.metrics) {
      capabilities.add("metrics-scrape");
    }
  }

  for (const dependency of topology.dependencies ?? []) {
    if (dependency.lifecycle !== "managed") continue;

    switch (dependency.kind) {
      case "postgres":
        capabilities.add("managed-postgres");
        break;
      case "redis":
        capabilities.add("managed-redis");
        break;
      case "object-storage":
        capabilities.add("managed-object-storage");
        break;
      case "secret-store":
        capabilities.add("secret-store");
        break;
      default:
        break;
    }
  }

  return [...capabilities].sort();
}

export function createPlatformCompatibilityReport(
  adapter: PlatformAdapter,
  deployment: DeploymentManifest
): PlatformCompatibilityReport {
  const requiredCapabilities = inferPlatformCapabilities(deployment);
  const supported = new Set(adapter.descriptor.capabilities);
  const missingCapabilities = requiredCapabilities.filter(
    (capability) => !supported.has(capability)
  );

  return {
    platformId: adapter.descriptor.id,
    compatible: missingCapabilities.length === 0,
    requiredCapabilities,
    missingCapabilities,
  };
}

function commandForWorkload(workload: DeploymentWorkload): string[] | undefined {
  if (workload.appPackage === "@constructorfleet/website" && workload.kind === "http") {
    return ["bash", "./docker/start-website-services.sh"];
  }

  if (!workload.startScript) return undefined;

  if (workload.appPackage) {
    return ["pnpm", "--filter", workload.appPackage, workload.startScript];
  }

  return ["pnpm", "run", workload.startScript];
}

function primaryPort(workload: DeploymentWorkload): number | undefined {
  return workload.ports?.[0]?.port;
}

function renderAwsWorkload(workload: DeploymentWorkload): PlatformRenderedWorkload {
  return {
    workloadId: workload.id,
    resourceType: workload.kind === "job" ? "ecs-task" : "ecs-service",
    resourceName: workload.id,
    settings: {
      appPackage: workload.appPackage ?? null,
      command: commandForWorkload(workload) ?? null,
      exposure: workload.exposure ?? (workload.kind === "http" ? "internal" : null),
      containerPort: primaryPort(workload) ?? null,
      ports: workload.ports ?? [],
      healthCheckPath: workload.ports?.[0]?.healthCheckPath ?? null,
      metrics: workload.metrics ?? null,
      scaling: workload.scaling ?? null,
    },
  };
}

function renderAwsDependency(dependency: DeploymentDependency): PlatformRenderedDependency {
  const resourceType =
    dependency.kind === "postgres"
      ? "rds-postgres"
      : dependency.kind === "redis"
        ? "elasticache-redis"
        : dependency.kind === "object-storage"
          ? "s3-bucket"
          : dependency.kind === "secret-store"
            ? "secrets-manager"
            : "eventbridge-bus";

  return {
    dependencyId: dependency.id,
    resourceType,
    resourceName: dependency.id,
    settings: {
      lifecycle: dependency.lifecycle ?? "self-hosted",
      required: dependency.required ?? false,
    },
  };
}

function renderRenderWorkload(workload: DeploymentWorkload): PlatformRenderedWorkload {
  const resourceType =
    workload.kind === "worker"
      ? "render-worker"
      : workload.exposure === "public"
        ? "render-web-service"
        : "render-private-service";

  return {
    workloadId: workload.id,
    resourceType,
    resourceName: workload.id,
    settings: {
      appPackage: workload.appPackage ?? null,
      command: commandForWorkload(workload) ?? null,
      port: primaryPort(workload) ?? null,
      ports: workload.ports ?? [],
      healthCheckPath: workload.ports?.[0]?.healthCheckPath ?? null,
      metrics: workload.metrics ?? null,
    },
  };
}

function renderRenderDependency(dependency: DeploymentDependency): PlatformRenderedDependency {
  const resourceType =
    dependency.kind === "postgres"
      ? "render-postgres"
      : dependency.kind === "redis"
        ? "render-redis"
        : dependency.kind === "object-storage"
          ? "external-object-storage"
          : dependency.kind === "secret-store"
            ? "render-env-group"
            : "external-event-bus";

  return {
    dependencyId: dependency.id,
    resourceType,
    resourceName: dependency.id,
    settings: {
      lifecycle: dependency.lifecycle ?? "self-hosted",
      required: dependency.required ?? false,
    },
  };
}

function swarmImageForWorkload(workload: DeploymentWorkload): string {
  if (
    workload.appPackage === "@constructorfleet/bhg-call" &&
    workload.startScript === "worker:processor-host"
  ) {
    return "${CF_PROCESSOR_IMAGE:-constructorfleet-bhg-processor:latest}";
  }

  return "${CF_APP_IMAGE:-constructorfleet:latest}";
}

function swarmInternalPorts(workload: DeploymentWorkload): DeploymentPort[] {
  const ports = [...(workload.ports ?? [])];

  if (
    workload.appPackage === "@constructorfleet/website" &&
    workload.kind === "http" &&
    !ports.some((port) => port.port === 4173)
  ) {
    ports.push({ name: "ui", port: 4173, protocol: "http" });
  }

  return ports;
}

function swarmPublishedPorts(workload: DeploymentWorkload) {
  if (workload.exposure !== "public") return [];

  if (workload.appPackage === "@constructorfleet/website" && workload.kind === "http") {
    return [{ publishedPort: 4173, targetPort: 4173 }];
  }

  const port = primaryPort(workload);
  return port ? [{ publishedPort: port, targetPort: port }] : [];
}

function renderSwarmWorkload(workload: DeploymentWorkload): PlatformRenderedWorkload {
  const internalPorts = swarmInternalPorts(workload);
  const publishedPorts = swarmPublishedPorts(workload);

  return {
    workloadId: workload.id,
    resourceType: "swarm-service",
    resourceName: workload.id,
    settings: {
      appPackage: workload.appPackage ?? null,
      command: commandForWorkload(workload) ?? null,
      image: swarmImageForWorkload(workload),
      exposure: workload.exposure ?? (workload.kind === "http" ? "internal" : null),
      internalPorts,
      publishedPorts,
      healthCheckPath: workload.ports?.[0]?.healthCheckPath ?? null,
      healthCheckPort: workload.ports?.[0]?.port ?? null,
      metrics: workload.metrics ?? null,
      replicas: workload.scaling?.minReplicas ?? 1,
      scaling: workload.scaling ?? null,
      dependencyIds: workload.dependencyIds ?? [],
    },
  };
}

function renderSwarmDependency(dependency: DeploymentDependency): PlatformRenderedDependency {
  const resourceType =
    dependency.kind === "postgres"
      ? "swarm-postgres"
      : dependency.kind === "redis"
        ? "swarm-redis"
        : dependency.kind === "object-storage"
          ? "external-object-storage"
          : dependency.kind === "secret-store"
            ? "swarm-secret-bundle"
            : "external-event-bus";

  return {
    dependencyId: dependency.id,
    resourceType,
    resourceName: dependency.id,
    settings: {
      lifecycle: dependency.lifecycle ?? "self-hosted",
      required: dependency.required ?? false,
    },
  };
}

type PlatformAdapterFactoryInput = {
  descriptor: PlatformDescriptor;
  renderWorkload: (workload: DeploymentWorkload) => PlatformRenderedWorkload;
  renderDependency: (dependency: DeploymentDependency) => PlatformRenderedDependency;
};

function createPlatformAdapter({
  descriptor,
  renderWorkload,
  renderDependency,
}: PlatformAdapterFactoryInput): PlatformAdapter {
  const adapter: PlatformAdapter = {
    descriptor,
    assess(deployment) {
      return createPlatformCompatibilityReport(adapter, deployment);
    },
    render(deployment) {
      const compatibility = adapter.assess(deployment);
      const topology = deployment.topology;

      return {
        platformId: descriptor.id,
        compatible: compatibility.compatible,
        missingCapabilities: compatibility.missingCapabilities,
        workloads: (topology?.workloads ?? []).map(renderWorkload),
        dependencies: (topology?.dependencies ?? []).map(renderDependency),
      };
    },
  };

  return adapter;
}

export function createAwsEcsFargateAdapter(): PlatformAdapter {
  return createPlatformAdapter({
    descriptor: {
      id: "aws-ecs-fargate",
      name: "AWS ECS on Fargate",
      capabilities: [
        "background-workers",
        "canary-deployments",
        "internal-service-network",
        "managed-object-storage",
        "managed-postgres",
        "managed-redis",
        "metrics-scrape",
        "public-ingress",
        "secret-store",
        "service-discovery",
      ],
    },
    renderWorkload: renderAwsWorkload,
    renderDependency: renderAwsDependency,
  });
}

export function createRenderPlatformAdapter(): PlatformAdapter {
  return createPlatformAdapter({
    descriptor: {
      id: "render",
      name: "Render",
      capabilities: [
        "background-workers",
        "internal-service-network",
        "managed-postgres",
        "metrics-scrape",
        "public-ingress",
        "secret-store",
        "service-discovery",
      ],
    },
    renderWorkload: renderRenderWorkload,
    renderDependency: renderRenderDependency,
  });
}

export function createDockerSwarmAdapter(): PlatformAdapter {
  return createPlatformAdapter({
    descriptor: {
      id: "docker-swarm",
      name: "Docker Swarm",
      capabilities: [
        "background-workers",
        "canary-deployments",
        "internal-service-network",
        "managed-object-storage",
        "managed-postgres",
        "managed-redis",
        "metrics-scrape",
        "public-ingress",
        "secret-store",
        "service-discovery",
      ],
    },
    renderWorkload: renderSwarmWorkload,
    renderDependency: renderSwarmDependency,
  });
}

export type { SubscriptionStatus } from "@constructorfleet/payments";

export type OfferingEnrollmentHook = {
  onEnroll?: (input: { userId: number; productId: string; data?: unknown }) => Promise<void>;
  onUnenroll?: (input: { userId: number; productId: string }) => Promise<void>;
};

export type OfferingWorkerDeclaration = {
  appPackage: string;
  startScript: string;
  port?: number;
};

export type OfferingManifestJson = {
  id: string;
  deploymentModule?: string;
  websitePluginModule?: string;
  catalogModule?: string;
  httpRoutesModule?: string;
  adminApiModule?: string;
  websiteApiModule?: string;
  notificationTemplatesModule?: string;
  enrollmentHookModule?: string;
  addonEnrollmentHookModule?: string;
  publicApiModule?: string;
  dbPackage?: string;
  migrationsPath?: string;
  workers?: OfferingWorkerDeclaration[];
};

export type OfferingNotificationTemplateContent = {
  subject?: string;
  headline: string;
  body: string;
  details?: string[];
  footer?: string;
  tag?: string;
};

export type OfferingNotificationTemplateRenderInput = {
  eventKey: string;
  payload: unknown;
  context?: Record<string, unknown>;
};

export type OfferingNotificationTemplateContributor = {
  eventKey: string;
  render: (
    input: OfferingNotificationTemplateRenderInput
  ) =>
    | OfferingNotificationTemplateContent
    | null
    | Promise<OfferingNotificationTemplateContent | null>;
};

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

export function isOfferingNotificationTemplateContent(
  value: unknown
): value is OfferingNotificationTemplateContent {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  if (typeof candidate.headline !== "string" || typeof candidate.body !== "string") {
    return false;
  }

  if (
    ("subject" in candidate &&
      candidate.subject !== undefined &&
      typeof candidate.subject !== "string") ||
    ("footer" in candidate &&
      candidate.footer !== undefined &&
      typeof candidate.footer !== "string") ||
    ("tag" in candidate && candidate.tag !== undefined && typeof candidate.tag !== "string") ||
    ("details" in candidate && candidate.details !== undefined && !isStringArray(candidate.details))
  ) {
    return false;
  }

  return true;
}

export interface Logger {
  debug(message: string, metadata?: Record<string, unknown>): void;
  info(message: string, metadata?: Record<string, unknown>): void;
  warn(message: string, metadata?: Record<string, unknown>): void;
  error(message: string, metadata?: Record<string, unknown>): void;
}

export interface Metrics {
  increment(name: string, value?: number, tags?: Record<string, string>): void;
  gauge(name: string, value: number, tags?: Record<string, string>): void;
  histogram(name: string, value: number, tags?: Record<string, string>): void;
}

export interface Trace {
  run<T>(spanName: string, fn: () => Promise<T>): Promise<T>;
}

// ---------------------------------------------------------------------------
// v2 offering interface
// ---------------------------------------------------------------------------

export interface ProductOffering {
  productId: string;
  displayName: string;
  description: string;
  manifest: ProductManifest;
  billing?: BillingConfig;
  deployment?: DeploymentManifest;
  productPage(ctx: OfferingContext): ReactElement;
  adminSettingsUI?(ctx: OfferingContext): ReactElement;
  userDashboardUI?(ctx: OfferingContext): ReactElement;
  loadSettings(ctx: OfferingContext): Promise<unknown>;
  saveSettings(ctx: OfferingContext, data: unknown): Promise<void>;
  registerRoutes?(): void;
  registerJobs?(): void;
}

// ---------------------------------------------------------------------------
// Marketing & routing
// ---------------------------------------------------------------------------

export type OfferingMarketingContent = {
  summary: string;
  description: string;
  primaryCtaLabel: string;
  /** Optional one-line supporting headline shown under the plan name on the pricing page. */
  tagline?: string;
  /** Optional feature bullets rendered in the pricing plan card. */
  highlights?: string[];
};

export type OfferingRoutes = {
  productPath: string;
  adminSettingsPath: string;
  signupPath?: string;
  demoPath?: string;
};

export type OfferingAdminNav = {
  navLabel: string;
  adminLinkLabel: string;
};

export type OfferingHttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "OPTIONS" | "HEAD";

export type OfferingRouteUserLevel = "public" | "user" | "admin";

export type OfferingHeaderRequirement = {
  key: string;
  value?: string;
};

export type OfferingRouteProxy = {
  target: string;
  preservePath?: boolean;
  targetPath?: string;
};

export type OfferingHttpRoute = {
  path: string;
  method: OfferingHttpMethod;
  accessLevel: OfferingRouteUserLevel;
  requiredHeaders?: OfferingHeaderRequirement[];
  bypassRateLimit?: boolean;
  proxy?: OfferingRouteProxy;
};

export type OfferingServerStartupContext = {
  /** Public base URL of the API server (e.g. a cloudflared tunnel URL), if available. */
  publicBaseUrl: string | null;
};

export type OfferingServerStartupHook = (context: OfferingServerStartupContext) => Promise<void>;

// ---------------------------------------------------------------------------
// Legacy v1 plugin contract
// ---------------------------------------------------------------------------

export type PublicApiEndpointParam = {
  name: string;
  in: "path" | "body" | "query";
  type: string;
  required: boolean;
  description: string;
};

export type PublicApiEndpoint = {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  summary: string;
  params?: PublicApiEndpointParam[];
  responseExample: string;
  errorCodes?: { code: number; description: string }[];
};

export type OpenApiSpec = Record<string, unknown>;

export function buildOpenApiSpec(
  offerings: {
    name: string;
    publicApiEndpoints?: { basePath: string; endpoints: PublicApiEndpoint[] };
  }[]
): OpenApiSpec {
  const paths: Record<string, Record<string, unknown>> = {};

  for (const offering of offerings) {
    const api = offering.publicApiEndpoints;
    if (!api) continue;

    for (const ep of api.endpoints) {
      const fullPath = (api.basePath + ep.path).replace(/:([a-zA-Z_]+)/g, "{$1}");
      const method = ep.method.toLowerCase();

      const parameters: Record<string, unknown>[] = [];
      const requestBodyProps: Record<string, unknown> = {};
      const requiredBodyProps: string[] = [];

      for (const p of ep.params ?? []) {
        if (p.in === "path" || p.in === "query") {
          parameters.push({
            name: p.name,
            in: p.in,
            required: p.required,
            description: p.description,
            schema: { type: p.type },
          });
        } else if (p.in === "body") {
          requestBodyProps[p.name] = { type: p.type, description: p.description };
          if (p.required) requiredBodyProps.push(p.name);
        }
      }

      let example: unknown;
      try {
        example = JSON.parse(ep.responseExample);
      } catch {
        example = ep.responseExample;
      }

      const responses: Record<string, unknown> = {
        "200": {
          description: "Successful response",
          content: { "application/json": { example } },
        },
      };
      for (const err of ep.errorCodes ?? []) {
        responses[String(err.code)] = {
          description: err.description,
          content: {
            "application/json": {
              example: { ok: false, error: err.description },
            },
          },
        };
      }

      const operation: Record<string, unknown> = {
        tags: [offering.name],
        summary: ep.summary,
        responses,
      };

      if (parameters.length > 0) operation.parameters = parameters;

      if (Object.keys(requestBodyProps).length > 0) {
        operation.requestBody = {
          required: true,
          content: {
            "application/json": {
              schema: {
                type: "object",
                properties: requestBodyProps,
                ...(requiredBodyProps.length > 0 ? { required: requiredBodyProps } : {}),
              },
            },
          },
        };
      }

      if (!paths[fullPath]) paths[fullPath] = {};
      paths[fullPath][method] = operation;
    }
  }

  return {
    openapi: "3.0.3",
    info: {
      title: "ConstructorFleet API",
      description: "Endpoints available with your API Access token.",
      version: "1.0.0",
    },
    servers: [{ url: "/", description: "This server" }],
    security: [{ BearerAuth: [] }],
    paths,
    components: {
      securitySchemes: {
        BearerAuth: {
          type: "http",
          scheme: "bearer",
          description: "API token created on the API Access settings page.",
        },
      },
    },
  };
}

// Legacy v1 contract kept for non-breaking migration during architecture rollout.
export type ProductOfferingPlugin<
  TDbClient = unknown,
  TSettings = unknown,
  TSaveResult = unknown,
  TRendered = unknown,
> = {
  id: string;
  name: string;
  marketing: OfferingMarketingContent;
  routes: OfferingRoutes;
  httpRoutes?: OfferingHttpRoute[];
  adminNav: OfferingAdminNav;
  addons?: ProductAddonPlugin<TDbClient, TRendered>[];
  publicApiEndpoints?: { basePath: string; endpoints: PublicApiEndpoint[] };
  loadSettings(dbClient: TDbClient): Promise<TSettings>;
  saveSettings(settings: TSettings, dbClient: TDbClient): Promise<TSaveResult>;
  adminUI(dbClient: TDbClient): TRendered;
  productPage(dbClient: TDbClient): TRendered;
  dashboardPage?(dbClient: TDbClient): TRendered;
  signupPage?(dbClient: TDbClient): TRendered;
  demoPage?(dbClient: TDbClient): TRendered;
};

export type ProductOfferingPluginModule<
  TDbClient = unknown,
  TSettings = unknown,
  TSaveResult = unknown,
  TRendered = unknown,
> =
  | {
      default: ProductOfferingPlugin<TDbClient, TSettings, TSaveResult, TRendered>;
    }
  | {
      plugin: ProductOfferingPlugin<TDbClient, TSettings, TSaveResult, TRendered>;
    };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function createRegistry<T extends Record<string, unknown>>(registry: T): T {
  return registry;
}

/**
 * Look up an add-on by ID across all add-on sources on a plugin.
 * Checks `billing.addons` (pricing-only declarations) and `addons`
 * (fully-wired plugin add-ons), returning the first match.
 */
export function findAddon(
  billing: BillingConfig | undefined,
  pluginAddons: ProductAddon[] | undefined,
  addonId: string
): ProductAddon | undefined {
  return (
    billing?.addons?.find((a) => a.id === addonId) ?? pluginAddons?.find((a) => a.id === addonId)
  );
}

export function formatProductPricing(pricing: ProductPricing): string {
  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: pricing.price % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(pricing.price / 100);

  if (pricing.billingModel === "usage" && (pricing.priceDimensions?.length ?? 0) > 0) {
    return `${formattedAmount}/${pricing.priceDimensions!.join(", ")}`;
  }

  switch (pricing.interval) {
    case "month":
      return `${formattedAmount}/month`;
    case "year":
      return `${formattedAmount}/year`;
    case "one-time":
      return formattedAmount;
  }
}
